import { api } from "../api.ts";
import { icon } from "../icons.ts";
import { day, relative, time, usd } from "../format.ts";
import { $, $$, arrowKeys, empty, errorText, esc, load, skeleton, toast } from "../ui.ts";
import { go, type ViewContext } from "../main.ts";
import { confirmationCard, wireConfirmations } from "./chat.ts";
import type { AuditEvent, Schedule, Task } from "../types.ts";

const TABS = [["pending", "Pendientes"], ["tasks", "Tareas"], ["scheduled", "Programado"], ["audit", "Auditoría"]] as const;
const KIND: Record<Schedule["kind"], [string, string]> = { reminder: ["alarm-clock", "Recordatorio"], task: ["clock", "Encargo"], watch: ["eye", "Vigilancia"] };
const REPEAT: Record<Schedule["repeat"], string> = { none: "", daily: " · todos los días", weekdays: " · de lunes a viernes" };
const SCHEDULE_STATUS: Record<Schedule["status"], string> = { active: "Activo", done: "Hecho", cancelled: "Cancelado" };
const TASK_STATUS: Record<Task["status"], string> = { running: "En curso", waiting: "Esperando tu confirmación", done: "Hecha", failed: "No se pudo", cancelled: "Cancelada" };
const OUTCOME_CLASS: Record<Task["steps"][number]["outcome"], string> = { ok: "ok", denied: "denied", error: "failed", waiting: "waiting" };
const LEVEL: Record<string, string> = { free: "libre", confirm: "confirmar", forbidden: "prohibido" };
const ACTOR: Record<AuditEvent["actor"], string> = { user: "Usuario", assistant: "Vera", admin: "Administración", system: "Sistema" };
const TOOL_ICON: Record<string, string> = { schedule: "alarm-clock", browser: "monitor", bookings: "calendar", payments: "credit-card", travel: "send", mail: "mail", calendar: "calendar", documents: "file-text", memory: "history", contacts: "users" };

export async function mount(main: HTMLElement, ctx: ViewContext) {
  const tab = TABS.some(([t]) => t === ctx.route.query.get("t")) ? ctx.route.query.get("t")! : "pending";
  main.innerHTML = `<div class="content wide">
    <div class="section-title"><div><h2>Lo que hace Vera</h2><p>Confirmaciones pendientes, tareas y el registro inalterable de todo.</p></div></div>
    <div class="segmented" role="tablist" aria-label="Secciones de actividad" style="margin-bottom:18px">
      ${TABS.map(([t, label]) => `<button role="tab" id="tab-${t}" aria-controls="panel" aria-selected="${t === tab}" tabindex="${t === tab ? 0 : -1}" data-tab="${t}">${label}</button>`).join("")}
    </div>
    <section id="panel" role="tabpanel" aria-labelledby="tab-${tab}"></section></div>`;
  const list = $(main, '[role="tablist"]');
  list.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((b) => b.addEventListener("click", () => go(`#/activity?t=${b.dataset.tab}`)));
  arrowKeys(list, (b) => go(`#/activity?t=${b.dataset.tab}`));
  const panel = $(main, "#panel");

  if (tab === "pending") {
    await load(panel, {
      placeholder: `<div style="display:grid;gap:14px">${skeleton(1, true)}${skeleton(1, true)}</div>`,
      fetch: () => api.confirmations(),
      render: ({ items }) => {
        const open = items.filter((c) => c.status === "pending");
        const rest = items.filter((c) => c.status !== "pending").slice(0, 20);
        panel.innerHTML = `${open.length ? `<div style="display:grid;gap:14px">${open.map((c) => confirmationCard(c)).join("")}</div>`
          : empty("inbox", "Nada pendiente", "Cuando Vera necesite tu permiso para pagar, reservar o escribir a alguien, lo verás aquí.")}
          ${rest.length ? `<h3 style="margin:28px 0 10px">Decididas</h3><div class="list">${rest.map((c) => `
            <div class="row">${icon(c.status === "approved" ? "check" : "x")}<div class="row-main"><strong>${esc(c.summary)}</strong>
            <span class="secondary">${esc(relative(c.decidedAt ?? c.createdAt))}</span></div>
            <span class="chip ${c.status}">${{ approved: "Confirmada", rejected: "Rechazada", expired: "Caducada", pending: "" }[c.status]}</span></div>`).join("")}</div>` : ""}`;
        wireConfirmations(panel, () => void ctx.refreshNav());
      },
    });
  }

  if (tab === "tasks") {
    await load(panel, {
      fetch: () => api.tasks(),
      render: ({ items }) => {
        panel.innerHTML = items.length ? `<div class="list">${items.map((t) => `
          <div class="row">${icon(TOOL_ICON[t.steps.at(-1)?.tool ?? ""] ?? "message-square")}
            <div class="row-main"><strong>${esc(t.title)}</strong>
              <span class="secondary">${esc(relative(t.startedAt))} · ${esc(usd(t.costMicroUsd))} de IA</span>
              ${t.steps.length ? `<div class="steps" aria-label="Pasos">${t.steps.map((s) => `<span class="chip ${OUTCOME_CLASS[s.outcome]}" title="Nivel: ${LEVEL[s.level]}">${esc(s.tool)} · ${esc(s.action)}</span>`).join("")}</div>` : ""}
            </div><span class="chip ${t.status}">${TASK_STATUS[t.status]}</span></div>`).join("")}</div>`
          : empty("list-checks", "Sin tareas todavía", "Aquí aparecerá cada cosa que le pidas a Vera, con los pasos que dio y lo que costó.");
      },
    });
  }

  if (tab === "scheduled") {
    const render = () => load(panel, {
      fetch: () => api.schedules(),
      render: ({ items }) => {
        panel.innerHTML = items.length ? `<div class="list">${items.map((s) => `
          <div class="row">${icon(KIND[s.kind][0])}
            <div class="row-main"><strong>${esc(s.title)}</strong>
              <span class="secondary">${KIND[s.kind][1]}${REPEAT[s.repeat]}${s.status === "active" && s.nextRunAt ? ` · ${s.kind === "watch" ? "vigilando" : "próxima"} ${s.kind === "watch" ? `hasta el ${esc(day(s.untilAt ?? s.nextRunAt))}` : `${esc(day(s.nextRunAt))}, ${esc(time(s.nextRunAt))}`}` : s.lastRunAt ? ` · ${esc(relative(s.lastRunAt))}` : ""}</span></div>
            ${s.status === "active" ? `<button class="btn ghost" type="button" data-cancel="${s.id}" aria-label="Cancelar: ${esc(s.title)}">${icon("x", "sm")} Cancelar</button>` : `<span class="chip ${s.status === "done" ? "done" : "cancelled"}">${SCHEDULE_STATUS[s.status]}</span>`}</div>`).join("")}</div>`
          : empty("alarm-clock", "Nada programado", "Pídele a Vera «recuérdame…», «cada mañana a las 8…» o «avísame si…» y aparecerá aquí.");
        $$<HTMLButtonElement>(panel, "[data-cancel]").forEach((b) => b.addEventListener("click", async () => {
          b.disabled = true;
          try { await api.cancelSchedule(b.dataset.cancel!); toast("Cancelado."); void render(); }
          catch (err) { b.disabled = false; toast(errorText(err), "error"); }
        }));
      },
    });
    await render();
  }

  if (tab === "audit") {
    await load(panel, {
      fetch: () => (ctx.me.user.role === "admin" ? api.admin.audit() : api.audit()),
      render: ({ items }) => {
        if (!items.length) { panel.innerHTML = empty("history", "El registro está vacío", "Cada inicio de sesión, permiso, confirmación y acción quedará aquí, encadenado para que no se pueda alterar."); return; }
        const types = [...new Set(items.map((e) => e.type))].sort();
        panel.innerHTML = `
          <div class="toolbar">
            <div class="field" style="min-width:200px"><label for="type-filter" class="sr-only">Filtrar por tipo</label>
              <select id="type-filter"><option value="">Todos los eventos</option>${types.map((t) => `<option>${esc(t)}</option>`).join("")}</select></div>
            <span class="spacer"></span>
            ${ctx.me.user.role === "admin" ? `<span class="chain-state" data-chain aria-live="polite"></span><button class="btn" type="button" data-verify>${icon("shield-check", "sm")} Verificar cadena</button>` : ""}
          </div>
          <div data-rows></div>`;
        const rows = (filter: string) => {
          const list = items.filter((e) => !filter || e.type === filter);
          $(panel, "[data-rows]").innerHTML = `
            <div class="table-wrap desktop-only"><table><caption class="sr-only">Registro de auditoría</caption>
              <thead><tr><th scope="col">Cuándo</th><th scope="col">Quién</th><th scope="col">Qué</th><th scope="col">Nivel</th><th scope="col">Hash</th></tr></thead>
              <tbody>${list.map((e) => `<tr><td class="num">${esc(day(e.at))}, ${esc(time(e.at))}</td><td>${ACTOR[e.actor]}</td>
                <td><span class="mono" style="font-size:12px;color:var(--ink-3)">${esc(e.type)}</span><br>${esc(e.description)}</td>
                <td>${e.level ? `<span class="chip ${e.level}">${LEVEL[e.level]}</span>` : ""}</td>
                <td><span class="mono hash" title="${e.hash}">${e.hash.slice(0, 10)}…</span></td></tr>`).join("")}</tbody></table></div>
            <div class="list mobile-only">${list.map((e) => `<div class="row"><div class="row-main">
              <span class="secondary">${esc(relative(e.at))} · ${ACTOR[e.actor]} · <span class="mono">${esc(e.type)}</span></span>
              <span>${esc(e.description)}</span><span class="mono hash secondary">${e.hash.slice(0, 16)}…</span></div>
              ${e.level ? `<span class="chip ${e.level}">${LEVEL[e.level]}</span>` : ""}</div>`).join("")}</div>`;
        };
        rows("");
        $<HTMLSelectElement>(panel, "#type-filter").addEventListener("change", (e) => rows((e.target as HTMLSelectElement).value));
        panel.querySelector<HTMLButtonElement>("[data-verify]")?.addEventListener("click", async (ev) => {
          const b = ev.currentTarget as HTMLButtonElement; const out = $(panel, "[data-chain]");
          b.disabled = true; out.className = "chain-state"; out.textContent = "Comprobando…";
          try {
            const r = await api.admin.verify();
            out.className = `chain-state ${r.intact ? "intact" : "broken"}`;
            out.innerHTML = r.intact ? `${icon("check", "sm")} Íntegra: ${r.events} eventos encadenados` : `${icon("circle-alert", "sm")} Rota en el evento ${r.firstBrokenId}`;
          } catch { out.className = "chain-state broken"; out.textContent = "No se ha podido comprobar"; }
          finally { b.disabled = false; }
        });
      },
    });
  }
}
